import { AxiosInstance } from 'axios';
import { ADMIN_RESOURCES, API_PREFIX } from '@shared/constants';
import { parseMultilingualData } from '@shared/utils';
import * as Types from './types';

// Helper function to handle pagination headers
function getPaginationFromHeaders(headers: any): { total: number } {
  const xTotalCount = headers['x-total-count'];
  if (xTotalCount) {
    return { total: parseInt(xTotalCount, 10) };
  }
  
  const contentRange = headers['content-range'];
  if (contentRange) {
    const match = contentRange.match(/\d+\-\d+\/(\d+)/);
    if (match) {
      return { total: parseInt(match[1], 10) };
    }
  }
  return { total: 0 };
}

// Helper function to build query params
function buildQueryParams(params?: Types.PaginationParams): string {
  if (!params) return '';
  
  const queryParts: string[] = [];
  
  if (params.range) {
    queryParts.push(`range=${JSON.stringify(params.range)}`);
  }
  
  if (params.sort) {
    queryParts.push(`sort=${JSON.stringify(params.sort)}`);
  }
  
  if (params.filter) {
    queryParts.push(`filter=${JSON.stringify(params.filter)}`);
  }
  
  return queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
}

// Helper to check if resource needs admin prefix
function needsAdminPrefix(resource: string): boolean {
  return ADMIN_RESOURCES.includes(resource as any);
}

// Helper to get resource path
function getResourcePath(resource: string, isAdmin: boolean): string {
  if (isAdmin && needsAdminPrefix(resource)) {
    return `${API_PREFIX.ADMIN}/${resource}`;
  }
  return `/${resource}`;
}

/**
 * Create an API client with the provided axios instance
 */
export function createApiClient(axios: AxiosInstance): Types.ApiClient {
  // Generic CRUD operations for admin resources
  const createAdminCrud = <T>(resource: string) => ({
    list: async (params?: Types.PaginationParams): Promise<Types.ListResponse<T>> => {
      const path = getResourcePath(resource, true);
      const query = buildQueryParams(params);
      const { data, headers } = await axios.get(`${path}${query}`);
      const { total } = getPaginationFromHeaders(headers);
      return { data, total: total || data.length };
    },
    
    get: async (id: string): Promise<T> => {
      const path = getResourcePath(resource, true);
      const { data } = await axios.get(`${path}/${id}`);
      return data;
    },
    
    create: async (payload: Partial<T>): Promise<T> => {
      const path = getResourcePath(resource, true);
      const { data } = await axios.post(path, payload);
      return data;
    },
    
    update: async (id: string, payload: Partial<T>): Promise<T> => {
      const path = getResourcePath(resource, true);
      const { data } = await axios.put(`${path}/${id}`, payload);
      return data;
    },
    
    delete: async (id: string): Promise<void> => {
      const path = getResourcePath(resource, true);
      await axios.delete(`${path}/${id}`);
    },
  });

  // Create CRUD instances for each admin resource
  const users = createAdminCrud<Types.User>('users');
  const packages = createAdminCrud<Types.Package>('packages');
  const userPackages = createAdminCrud<Types.UserPackage>('user_packages');
  const subscriptions = createAdminCrud<Types.Subscription>('subscriptions');
  const userSubscriptions = createAdminCrud<Types.UserSubscription>('user_subscriptions');
  const bookings = createAdminCrud<Types.Booking>('bookings');
  const giftCards = createAdminCrud<Types.GiftCard>('gift-cards');
  const giftCardTransactions = createAdminCrud<any>('gift-card-transactions');
  const payments = createAdminCrud<Types.Payment>('payments');
  const paymentItems = createAdminCrud<any>('payment_items');
  const promotions = createAdminCrud<Types.Promotion>('promotions');
  const sessionTransactions = createAdminCrud<Types.SessionTransaction>('session-transactions');
  const adminUsers = createAdminCrud<Types.AdminUser>('admin_users');
  const auditLogs = createAdminCrud<Types.AuditLog>('audit-logs');
  const resources = createAdminCrud<Types.Resource>('resources');
  const resourceWorkingHours = createAdminCrud<any>('resource-working-hours');
  const resourceClosingPeriods = createAdminCrud<any>('resource-closing-periods');
  const services = createAdminCrud<Types.Service>('services');
  const contentPages = createAdminCrud<any>('content-pages');

  return {
    // User Management
    getCurrentUser: async () => {
      const { data } = await axios.get('/api/users/me');
      return data;
    },
    
    getUserProfile: async () => {
      const { data } = await axios.get('/api/users/profile');
      return data;
    },
    
    updateUserProfile: async (params) => {
      const { data } = await axios.put('/api/users/profile', params);
      return data;
    },
    
    completeRegistration: async (params) => {
      await axios.post('/api/users/complete-registration', params);
    },
    
    verifyEmail: async (params) => {
      await axios.post('/api/users/verify-email', params);
    },
    
    // Admin User Management
    getUsers: users.list,
    getUser: users.get,
    createUser: users.create,
    updateUser: users.update,
    deleteUser: users.delete,
    
    // Packages
    getPackages: async () => {
      const { data } = await axios.get('/api/v1/bookla/packages');
      return data;
    },
    
    getPackage: async (id) => {
      const { data } = await axios.get(`/api/v1/bookla/packages/${id}`);
      return data;
    },
    
    getUserPackages: async () => {
      const { data } = await axios.get('/api/users/me/packages');
      return data;
    },
    
    // Admin Packages
    getAdminPackages: packages.list,
    getAdminPackage: packages.get,
    createAdminPackage: packages.create,
    updateAdminPackage: packages.update,
    deleteAdminPackage: packages.delete,
    
    // User Packages (Admin)
    getAdminUserPackages: userPackages.list,
    getAdminUserPackage: userPackages.get,
    createAdminUserPackage: userPackages.create,
    updateAdminUserPackage: userPackages.update,
    deleteAdminUserPackage: userPackages.delete,
    
    // Subscriptions
    getUserSubscriptions: async () => {
      const { data } = await axios.get('/api/users/me/subscriptions');
      return data;
    },
    
    cancelSubscription: async (subscriptionId) => {
      const { data } = await axios.post('/api/users/me/cancel-subscription', { subscriptionId });
      return data;
    },
    
    // Admin Subscriptions
    getAdminSubscriptions: subscriptions.list,
    getAdminSubscription: subscriptions.get,
    createAdminSubscription: subscriptions.create,
    updateAdminSubscription: subscriptions.update,
    deleteAdminSubscription: subscriptions.delete,
    
    // User Subscriptions (Admin)
    getAdminUserSubscriptions: userSubscriptions.list,
    getAdminUserSubscription: userSubscriptions.get,
    createAdminUserSubscription: userSubscriptions.create,
    updateAdminUserSubscription: userSubscriptions.update,
    deleteAdminUserSubscription: userSubscriptions.delete,
    
    // Services
    getServices: async () => {
      const { data } = await axios.get('/api/services');
      return data;
    },
    
    getService: async (id) => {
      const { data } = await axios.get(`/api/services/${id}`);
      return data;
    },
    
    getServiceAvailability: async (id, params) => {
      const { data } = await axios.get(`/api/services/${id}/availability`, { params });
      return data;
    },
    
    getServiceTimes: async (serviceId, params) => {
      // Note: This endpoint now uses local database instead of Bookla API
      const { data } = await axios.get(`/api/v1/bookla/services/${serviceId}/times`, { params });
      return data;
    },
    
    // Admin Services
    getAdminServices: services.list,
    getAdminService: services.get,
    createAdminService: services.create,
    updateAdminService: services.update,
    deleteAdminService: services.delete,
    
    // Bookings
    getBookings: async (params) => {
      const { data } = await axios.get('/api/bookings', { params });
      return data;
    },
    
    createBooking: async (params) => {
      const { data } = await axios.post('/api/bookings', params);
      return data;
    },
    
    createBulkBookings: async (params) => {
      const { data } = await axios.post('/api/bookings/bulk', params);
      return data;
    },
    
    rescheduleBooking: async (id, params) => {
      const { data } = await axios.put(`/api/bookings/${id}`, params);
      return data;
    },
    
    cancelBooking: async (id, params) => {
      await axios.delete(`/api/bookings/${id}`, { data: params });
    },
    
    // Admin Bookings
    getAdminBookings: bookings.list,
    getAdminBooking: bookings.get,
    createAdminBooking: bookings.create,
    updateAdminBooking: bookings.update,
    deleteAdminBooking: bookings.delete,
    
    sendBookingReminder: async (id) => {
      await axios.post(`/api/admin/bookings/${id}/send-reminder`);
    },
    
    getContractBookings: async (contractId) => {
      const { data } = await axios.get(`/api/admin/contract-bookings/${contractId}`);
      return data;
    },
    
    // Gift Cards
    createGiftCard: async (params) => {
      const { data } = await axios.post('/api/gift-cards/create', params);
      return data;
    },
    
    getGiftCardBalance: async (code) => {
      const { data } = await axios.get(`/api/gift-cards/balance/${code}`);
      return data;
    },
    
    redeemGiftCard: async (params) => {
      await axios.post('/api/gift-cards/redeem', params);
    },
    
    purchaseGiftCard: async (amount) => {
      const { data } = await axios.post('/api/gift-cards/purchase', { amount });
      return data;
    },
    
    confirmGiftCardPurchase: async (params) => {
      const { data } = await axios.post('/api/gift-cards/confirm-purchase', params);
      return data;
    },
    
    // Admin Gift Cards
    getAdminGiftCards: giftCards.list,
    getAdminGiftCard: giftCards.get,
    createAdminGiftCard: giftCards.create,
    updateAdminGiftCard: giftCards.update,
    deleteAdminGiftCard: giftCards.delete,
    
    // Gift Card Transactions (Admin)
    getAdminGiftCardTransactions: giftCardTransactions.list,
    getAdminGiftCardTransaction: giftCardTransactions.get,
    createAdminGiftCardTransaction: giftCardTransactions.create,
    updateAdminGiftCardTransaction: giftCardTransactions.update,
    deleteAdminGiftCardTransaction: giftCardTransactions.delete,
    
    // Payments
    createPaymentIntent: async (params) => {
      const { data } = await axios.post('/api/payments/create-payment-intent', params);
      return data;
    },
    
    createSubscriptionIntent: async (params) => {
      const { data } = await axios.post('/api/payments/create-subscription-intent', params);
      return data;
    },
    
    confirmSubscription: async (params) => {
      await axios.post('/api/payments/confirm-subscription', params);
    },
    
    createBookingPaymentIntent: async (params) => {
      const { data } = await axios.post('/api/payments/create-booking-payment-intent', params);
      return data;
    },
    
    createContract: async (params) => {
      const { data } = await axios.post('/api/payments/create-contract', params);
      return data;
    },
    
    // Admin Payments
    getAdminPayments: payments.list,
    getAdminPayment: payments.get,
    createAdminPayment: payments.create,
    updateAdminPayment: payments.update,
    deleteAdminPayment: payments.delete,
    
    // Payment Items (Admin)
    getAdminPaymentItems: paymentItems.list,
    getAdminPaymentItem: paymentItems.get,
    createAdminPaymentItem: paymentItems.create,
    updateAdminPaymentItem: paymentItems.update,
    deleteAdminPaymentItem: paymentItems.delete,
    
    // Stripe Transactions (Admin)
    getAdminStripeTransactions: async (params) => {
      const query = buildQueryParams(params);
      const { data, headers } = await axios.get(`/api/admin/stripe-transactions${query}`);
      const { total } = getPaginationFromHeaders(headers);
      return { data, total: total || data.length };
    },

    // Admin Promotions (with special handling for multilingual fields)
    getAdminPromotions: async (params) => {
      const response = await promotions.list(params);
      // Transform multilingual fields
      response.data = response.data.map(promo => ({
        ...promo,
        title: parseMultilingualData(promo.title as any) || promo.title,
        description: parseMultilingualData(promo.description as any) || promo.description,
        package_title: parseMultilingualData(promo.package_title as any) || promo.package_title,
      }));
      return response;
    },
    
    getAdminPromotion: async (id) => {
      const promo = await promotions.get(id);
      // Transform multilingual fields
      return {
        ...promo,
        title: parseMultilingualData(promo.title as any) || promo.title,
        description: parseMultilingualData(promo.description as any) || promo.description,
        package_title: parseMultilingualData(promo.package_title as any) || promo.package_title,
      };
    },
    
    createAdminPromotion: promotions.create,
    updateAdminPromotion: promotions.update,
    deleteAdminPromotion: promotions.delete,
    
    // Session Transactions (Admin)
    getAdminSessionTransactions: sessionTransactions.list,
    getAdminSessionTransaction: sessionTransactions.get,
    createAdminSessionTransaction: sessionTransactions.create,
    updateAdminSessionTransaction: sessionTransactions.update,
    deleteAdminSessionTransaction: sessionTransactions.delete,
    
    // Admin Stats & Reports
    getAdminStats: async () => {
      const { data } = await axios.get('/api/admin/stats');
      return data;
    },
    
    getAdminReport: async (reportType, params) => {
      const { data } = await axios.get(`/api/admin/reports/${reportType}`, { params });
      return data;
    },
    
    // Admin Users
    getAdminUsers: adminUsers.list,
    getAdminUser: adminUsers.get,
    createAdminUser: adminUsers.create,
    updateAdminUser: adminUsers.update,
    deleteAdminUser: adminUsers.delete,
    
    // Audit Logs (Admin)
    getAdminAuditLogs: auditLogs.list,
    getAdminAuditLog: auditLogs.get,
    createAdminAuditLog: auditLogs.create,
    updateAdminAuditLog: auditLogs.update,
    deleteAdminAuditLog: auditLogs.delete,
    
    // Resources (Admin)
    getAdminResources: resources.list,
    getAdminResource: resources.get,
    createAdminResource: resources.create,
    updateAdminResource: resources.update,
    deleteAdminResource: resources.delete,
    
    // Resource Working Hours (Admin)
    getAdminResourceWorkingHours: resourceWorkingHours.list,
    getAdminResourceWorkingHour: resourceWorkingHours.get,
    createAdminResourceWorkingHours: resourceWorkingHours.create,
    updateAdminResourceWorkingHour: resourceWorkingHours.update,
    deleteAdminResourceWorkingHour: resourceWorkingHours.delete,
    
    // Resource Closing Periods (Admin)
    getAdminResourceClosingPeriods: resourceClosingPeriods.list,
    getAdminResourceClosingPeriod: resourceClosingPeriods.get,
    createAdminResourceClosingPeriods: resourceClosingPeriods.create,
    updateAdminResourceClosingPeriod: resourceClosingPeriods.update,
    deleteAdminResourceClosingPeriod: resourceClosingPeriods.delete,
    
    // Content Pages (Admin)
    getAdminContentPages: contentPages.list,
    getAdminContentPage: contentPages.get,
    createAdminContentPage: contentPages.create,
    updateAdminContentPage: contentPages.update,
    deleteAdminContentPage: contentPages.delete,
    
    // Price Management (Admin)
    getPrices: async (resource, resourceId) => {
      const { data } = await axios.get(`/api/admin/${resource}/${resourceId}/prices`);
      return data;
    },
    
    createPrice: async (resource, resourceId, data) => {
      const { data: result } = await axios.post(`/api/admin/${resource}/${resourceId}/prices`, data);
      return result;
    },
    
    updatePrice: async (resource, priceId, data) => {
      const { data: result } = await axios.put(`/api/admin/${resource}/prices/${priceId}`, data);
      return result;
    },
    
    deletePrice: async (resource, priceId) => {
      await axios.delete(`/api/admin/${resource}/prices/${priceId}`);
    },
    
    // Batch Operations (Admin)
    updateMany: async <T>(resource: string, ids: string[], data: Partial<T>) => {
      const path = getResourcePath(resource, true);
      const filter = { id: ids };
      const { data: result } = await axios.put(`${path}?filter=${JSON.stringify(filter)}`, data);
      return result;
    },
    
    deleteMany: async (resource, ids) => {
      const path = getResourcePath(resource, true);
      const filter = { id: ids };
      await axios.delete(`${path}?filter=${JSON.stringify(filter)}`);
    },
  };
}

// Re-export types
export * from './types';